import { useNavigate } from "react-router"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { EyeIcon, PencilIcon } from "../../icons"
import { useState } from "react"

type GroupSeedbedSummary = { id: string; name: string }

type Group = {
  id: string
  name: string
  description?: string | null
  groupType: "individual" | "shared" | "team" | "edition"
  cccdTeamId?: string | null
  traCertificationEditionId?: string | null
  year?: number | null
  createdAt: string
  students_count: number
  seedbeds: GroupSeedbedSummary[]
}

const GROUP_TYPE_LABELS: Record<Group["groupType"], string> = {
  individual: "Individual",
  shared: "Compartido",
  team: "Equipo CCCD",
  edition: "Edición Tripoli",
}

const GROUP_TYPE_BADGE: Record<Group["groupType"], string> = {
  individual: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  shared: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  team: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  edition: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
}

/**
 * Gestor global de grupos (seedbed-group-manager): un grupo agrupa
 * estudiantes una sola vez y puede asignarse a varios semilleros, en vez de
 * recrearse por semillero. Vista completa (no modal), como el gestor de
 * estudiantes — vive justo debajo de él en el menú.
 */
export default function GroupsManager() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<"" | Group["groupType"]>("")

  const {
    rows: groups,
    total: groupsTotal,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error,
    reload,
  } = useServerTable<Group>({
    endpoint: "/academy/seedbeds/admin/groups",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    // El tipo viaja al servidor: el total y la paginación son reales con cualquier cantidad de grupos.
    params: { search, groupType: typeFilter || undefined },
    extractRows: (data) => data.groups ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  const columns: DataTableColumn<Group>[] = [
    {
      key: "name",
      label: "Grupo",
      sortable: true,
      render: (group) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{group.name}</p>
          {group.description && <p className="text-xs text-gray-500">{group.description}</p>}
        </>
      ),
    },
    {
      key: "groupType",
      label: "Tipo",
      render: (group) => (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${GROUP_TYPE_BADGE[group.groupType]}`}>
          {GROUP_TYPE_LABELS[group.groupType]}
          {group.groupType === "team" && group.year ? ` · ${group.year}` : ""}
        </span>
      ),
    },
    {
      key: "seedbeds",
      label: "Semilleros",
      render: (group) =>
        group.seedbeds.length === 0 ? (
          <span className="text-xs text-gray-500 dark:text-gray-400 italic">sin asignar</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {group.seedbeds.map((s) => (
              <span key={s.id} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {s.name}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: "students_count",
      label: "Estudiantes",
      render: (group) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {group.students_count} estudiante{group.students_count !== 1 ? "s" : ""}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Creado",
      sortable: true,
      render: (group) => (
        <span className="text-xs text-gray-500">{new Date(group.createdAt).toLocaleDateString("es-CO")}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (group) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: group.groupType === "team" || group.groupType === "edition" ? "Ver grupo" : "Editar grupo",
                icon: group.groupType === "team" || group.groupType === "edition" ? <EyeIcon /> : <PencilIcon />,
                onClick: () => navigate(`/instructor/groups/${group.id}`),
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Docentes", "Grupos"]}
      title="Grupos"
      subtitle="Agrupa estudiantes una sola vez y asígnalos a uno o varios semilleros"
      tabs={[
        { label: "Panel", to: "/instructor/hub" },
        { label: "Entregas", to: "/instructor/submissions" },
        { label: "Exámenes IA", to: "/instructor/section-quizzes" },
        { label: "Estudiantes", to: "/instructor/students" },
        { label: "Grupos", to: "/instructor/groups", active: true },
      ]}
      metrics={[{ label: "Total grupos", value: String(groupsTotal) }]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar grupo por nombre...",
        },
        filters: [
          {
            type: "select",
            key: "groupType",
            label: "Tipo",
            value: typeFilter,
            onChange: (v) => setTypeFilter(v as "" | Group["groupType"]),
            options: [
              { label: "Todos", value: "" },
              ...(Object.entries(GROUP_TYPE_LABELS) as [Group["groupType"], string][]).map(([value, label]) => ({
                label,
                value,
              })),
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,
        onCreate: () => navigate("/instructor/groups/new"),
        createLabel: "+ Crear grupo",
      }}
    >
      {error && (
        <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400 mb-4">
          {error}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={groups}
        rowKey={(group) => group.id}
        loading={loading}
        emptyMessage="No se encontraron grupos."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={groupsTotal}
        onPageChange={setPage}
      />
    </ModuleLayout>
  )
}
